import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, X, Apple, Sparkles } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const DISMISS_KEY = "get_mobile_app_banner_dismissed_at";
const VARIANT_KEY = "get_mobile_app_banner_variant";
const DISMISS_DAYS = 30;

// Update these once the apps are live in the stores.
const APP_STORE_URL = "https://apps.apple.com/app/sacred-celestial-flow/id000000000";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.sacredcelestialflow.app";
// Universal smart link — works on both iOS and Android (redirects to correct store).
const UNIVERSAL_LINK = "https://sacredcelestialflow.com/get-app";

// --- A/B/C variants ---
// Sticky per-visitor via localStorage. Add new variants here — admin dashboard
// automatically picks them up because it groups by metadata.variant.
const VARIANTS = {
  a_calm: {
    eyebrow: "New",
    headline: "Your cycle, on the go.",
    subhead:
      "Get the Sacred Celestial Flow mobile app — same sacred practice, now in your pocket with offline access & native notifications.",
  },
  b_benefit: {
    eyebrow: "Free upgrade",
    headline: "Track your cycle in 2 taps.",
    subhead:
      "Native push reminders, offline journaling, and quick-log widgets — all in the free Sacred Celestial Flow mobile app.",
  },
  c_urgency: {
    eyebrow: "Limited preview",
    headline: "Never miss your next phase.",
    subhead:
      "Be the first to try kick-counter widgets, lock-screen mood logging, and home-screen rituals on iOS & Android.",
  },
};
const VARIANT_KEYS = Object.keys(VARIANTS);

// Pick a variant (sticky) for this visitor. Even random assignment is fine —
// admin dashboard aggregates by metadata.variant.
const pickVariant = () => {
  try {
    const stored = localStorage.getItem(VARIANT_KEY);
    if (stored && VARIANTS[stored]) return stored;
  } catch {
    /* ignore */
  }
  const v = VARIANT_KEYS[Math.floor(Math.random() * VARIANT_KEYS.length)];
  try {
    localStorage.setItem(VARIANT_KEY, v);
  } catch {
    /* ignore */
  }
  return v;
};

// Detect if the visitor is already on a mobile device — banner is for web users only.
const isOnMobileDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
};

const PlayStoreBadge = () => (
  // Inline SVG mimic of the Play Store badge (Google's badge has licensing rules)
  <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
    <path
      fill="currentColor"
      d="M3 3.5v17l11.5-8.5L3 3.5zm12.5 8.7l3.4-2.5c.6-.5.6-1.4 0-1.8L15.5 5.5l-2.6 1.9 2.6 4.8zm-.6.6L4.6 19.6c-.4.3-.9.3-1.3.1l9.5-7 1.4 1.4.7 1.7zm.6 1.6 3.4 2.5c.6.4.6 1.3 0 1.8l-3.4 2.5-2.6-4.8 2.6-2z"
    />
  </svg>
);

const GetMobileAppBanner = () => {
  const [visible, setVisible] = useState(false);
  const variant = useMemo(() => pickVariant(), []);
  const copy = VARIANTS[variant];

  useEffect(() => {
    if (isOnMobileDevice()) return;
    try {
      const dismissedAt = localStorage.getItem(DISMISS_KEY);
      if (dismissedAt) {
        const days = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
        if (days < DISMISS_DAYS) return;
      }
    } catch {
      /* ignore */
    }
    setVisible(true);
    trackEvent("mobile_app_banner_shown", "dashboard", { variant });
  }, [variant]);

  const handleDismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    trackEvent("mobile_app_banner_dismissed", "dashboard", { variant });
  };

  const handleStoreClick = (store) => {
    trackEvent("mobile_app_link_clicked", "dashboard", { store, variant });
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.35 }}
        data-testid="get-mobile-app-banner"
        data-variant={variant}
        className="relative overflow-hidden rounded-3xl border border-fuchsia-400/30 bg-gradient-to-r from-fuchsia-900/40 via-purple-900/40 to-indigo-900/40 p-5 md:p-6 shadow-xl"
      >
        {/* Decorative glow */}
        <div aria-hidden className="absolute -top-12 -right-10 w-48 h-48 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div aria-hidden className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-indigo-500/20 blur-3xl" />

        <button
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          className="absolute top-3 right-3 text-mystic-200/60 hover:text-white transition-colors"
          data-testid="dismiss-mobile-app-banner"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative flex flex-col md:flex-row items-center gap-5 md:gap-8">
          {/* Left: Headline + CTA buttons */}
          <div className="flex-1 text-center md:text-left">
            <p className="inline-flex items-center gap-1.5 text-fuchsia-200 text-xs uppercase tracking-widest mb-2">
              <Sparkles className="w-3.5 h-3.5" />
              {copy.eyebrow}
            </p>
            <h3 className="text-white text-xl md:text-2xl font-semibold leading-tight">
              {copy.headline}
            </h3>
            <p className="text-mystic-100/80 text-sm mt-1.5 max-w-md mx-auto md:mx-0">
              {copy.subhead}
            </p>

            <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-4">
              <a
                href={APP_STORE_URL}
                onClick={() => handleStoreClick("app_store")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/80 hover:bg-black text-white text-sm font-medium transition-all border border-white/10"
                data-testid="mobile-app-appstore-btn"
              >
                <Apple className="w-5 h-5" />
                <span>
                  <span className="block text-[10px] leading-none text-mystic-200">Download on the</span>
                  <span className="block leading-tight">App Store</span>
                </span>
              </a>

              <a
                href={PLAY_STORE_URL}
                onClick={() => handleStoreClick("play_store")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/80 hover:bg-black text-white text-sm font-medium transition-all border border-white/10"
                data-testid="mobile-app-playstore-btn"
              >
                <PlayStoreBadge />
                <span>
                  <span className="block text-[10px] leading-none text-mystic-200">Get it on</span>
                  <span className="block leading-tight">Google Play</span>
                </span>
              </a>
            </div>
          </div>

          {/* Right: QR code panel */}
          <div className="flex items-center gap-3" data-testid="mobile-app-qr-panel">
            <div className="hidden md:flex flex-col items-end pr-1">
              <Smartphone className="w-5 h-5 text-fuchsia-300" />
              <p className="text-mystic-200/70 text-[11px] mt-1 text-right max-w-[8rem] leading-snug">
                Scan with your phone camera
              </p>
            </div>
            <div className="rounded-2xl bg-white p-2.5 shadow-2xl">
              <QRCodeSVG
                value={UNIVERSAL_LINK}
                size={92}
                level="M"
                fgColor="#0f0518"
                bgColor="#ffffff"
                aria-label="QR code linking to the Sacred Celestial Flow mobile app"
              />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GetMobileAppBanner;
